/**
 * المرحلة 1.35 — the centre's printed consents (photos 5–9), filled from the
 * file: the child, the guardian, the national ID, the phone, the address, the
 * centre's name and the hour; the signatures and the thumbprints stay blank.
 *
 *   admission       «إقرار بالموافقة على دخول …»                       photo 5
 *   on_request      «إقرار خروج حسب الطلب»                              photo 6
 *   recovered       «إقرار استلام حالة شفاء»                            photo 7
 *   death           «إقرار استلام حالة وفاة»                            photo 7
 *   against_advice  «إقرار خروج من … بناءً على طلب»                     photo 7
 *   central_line    «إقرار» for a central line                          photo 8
 *   payment         «تعهد» to pay the stay's costs                      photo 8
 *   prices          the price list and «إقرار» (the live prices)        photo 9
 *
 * The wording lives in the dictionaries (`consent.<id>.body`), with a
 * placeholder for each value; `consentParts` cuts it into text and values so
 * a value prints bold on its dotted line, and a missing one prints the line.
 */

/** The consents, in the booklet's order. */
export const CONSENTS = Object.freeze(['admission', 'on_request', 'recovered', 'death', 'against_advice', 'central_line', 'payment', 'prices']);

/** The values each consent's wording names. */
export const CONSENT_FIELDS = Object.freeze({
  admission: ['guardian', 'child', 'relation', 'address', 'centre'],
  on_request: ['guardian', 'child', 'nationalId', 'phone', 'address'],
  recovered: ['guardian', 'sonOrDaughter', 'child', 'day', 'date', 'time', 'instructions'],
  death: ['guardian', 'sonOrDaughter', 'child', 'deceased', 'day', 'date', 'time'],
  against_advice: ['guardian', 'relation', 'child', 'centre'],
  central_line: ['guardian', 'child', 'procedure'],
  payment: ['guardian', 'nationalId', 'relation', 'child', 'day', 'date', 'time', 'centre', 'address'],
  prices: ['guardian', 'child', 'centre'],
});

/** The signature lines under each consent, left blank. */
export const CONSENT_SIGNS = Object.freeze({
  admission: ['guardianSignature', 'nationalId', 'date'],
  on_request: ['guardianName', 'signature', 'nationalId', 'phone', 'date'],
  recovered: ['name', 'idCard', 'signature', 'kinship', 'receptionist'],
  death: ['name', 'idCard', 'signature', 'kinship', 'receptionist'],
  against_advice: ['name', 'idCard', 'signature'],
  central_line: ['name', 'signature', 'capacity', 'phone'],
  payment: ['signatureTrue', 'receptionist', 'sign'],
  prices: ['guardianName', 'nationalId', 'phone', 'date'],
});

/** The consents that carry the child's thumbprints (photo 6). */
export const THUMBS = Object.freeze(['on_request']);

/** The consent that goes with each way out of a stay (a transfer has none). */
export const CONSENT_FOR_DISCHARGE = Object.freeze({ recovered: 'recovered', on_request: 'on_request', against_advice: 'against_advice', death: 'death' });

/** The moment a consent names: the admission for the undertaking, the discharge for the hand-over, else now. */
export function momentOf(id, stay, now = new Date()) {
  if (id === 'payment') return stay && stay.admittedAt ? stay.admittedAt : now;
  if (id === 'recovered' || id === 'death') return stay && stay.dischargedAt ? stay.dischargedAt : now;
  return now;
}

/**
 * The values a consent is filled with. Formatting is the caller's (the
 * institution's clock and language): `fmt = { date, time, weekday }`, and
 * `t` for the words that change with the child's sex and the relation.
 */
export function consentValues(id, { patient = {}, profile = {}, stay = null, centre = '', procedure = '', now = new Date() } = {}, { t = (k) => k, fmt = {} } = {}) {
  const at = momentOf(id, stay, now);
  const girl = patient.gender === 'female';
  const pick = (...vals) => vals.find((v) => v !== undefined && v !== null && String(v).trim() !== '') ?? null;
  return {
    guardian: pick(profile.guardianName, patient.guardianName),
    child: pick(patient.fullName),
    relation: pick(profile.guardianRelation, patient.guardianRelation) ? t(`patientFile.rel.${pick(profile.guardianRelation, patient.guardianRelation)}`) : null,
    nationalId: pick(profile.guardianNationalId, patient.guardianNationalId, patient.nationalId),
    phone: pick(profile.guardianPhone, patient.guardianPhone, patient.phonePrimary),
    address: pick(profile.address, patient.address),
    centre: pick(centre),
    procedure: pick(procedure),
    // The doctor's instructions after recovery are written by hand on the line.
    instructions: null,
    sonOrDaughter: patient.gender ? t(girl ? 'centerConsent.word.daughter' : 'centerConsent.word.son') : t('centerConsent.word.sonOrDaughter'),
    deceased: t(girl ? 'centerConsent.word.deceasedF' : 'centerConsent.word.deceasedM'),
    day: fmt.weekday ? fmt.weekday(at) : null,
    date: fmt.date ? fmt.date(at) : null,
    time: fmt.time ? fmt.time(at) : null,
  };
}

/**
 * A consent's wording cut into its text and its values:
 * [{ text }, { field, value }] — `value` null where the paper keeps the line.
 */
export function consentParts(template, values = {}) {
  const parts = [];
  const re = /\{(\w+)\}/g;
  let last = 0;
  let m;
  const text = String(template || '');
  while ((m = re.exec(text))) {
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    const v = values[m[1]];
    parts.push({ field: m[1], value: v === undefined || v === null || String(v).trim() === '' ? null : String(v) });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** The consent as plain text, a missing value as a dotted line (for a test, or a copy). */
export const consentText = (template, values, blank = '..........') =>
  consentParts(template, values)
    .map((p) => (p.field ? p.value || blank : p.text))
    .join('');

/**
 * The price list as the acknowledgement prints it: each category with its
 * priced items, from the live list (`staysApi.options().units`).
 */
export function priceTable(leaves = []) {
  const byCategory = new Map();
  for (const l of leaves) {
    const name = l.categoryName || (l.path && l.path[0]) || '';
    if (!byCategory.has(name)) byCategory.set(name, []);
    byCategory.get(name).push({ label: (l.path || [l.name]).slice(1).join(' — ') || l.name, price: l.price, unit: l.unit });
  }
  return Array.from(byCategory.entries()).map(([category, items]) => ({ category, items }));
}
